/**
 * 오더 상세 시트 ($j.F.Mobile.OrderSheet · BS 타겟)
 *
 * 내역 화면에서 한 줄을 누르면 그 오더의 작업내용과 **그 오더에 걸린 내역만 모아서** 보여준다.
 * 내역 목록에 '오더' 칸을 따로 두고 번호로 필터하던 것을 대신한다 — 회원에게 오더번호는
 * 아무 정보가 아니고, 모아보기는 오더를 열었을 때 같이 보이는 편이 찾기 쉽다.
 *
 * 호출:
 *   $j.Ui.BS.open({ target: '$j.F.Mobile.OrderSheet', fixedHeight: true, data: { ordernum, logs: 'point' } });
 *     ordernum  오더번호
 *     logs      아래 LOGS 의 키 — 모아볼 내역 종류. 없으면 작업내용만 그린다
 *
 * 쓰는 곳: 수주포인트(/m/point) · 사용내역(/m/cash/list) · -6차(/m/msix, 작업내용만)
 */
import Util from '../../Common/Util';

/**
 * 모아볼 내역 종류.
 * load() 는 일어난 순서대로 [{at, date, label(HTML), amount, hold?}] 를 돌려준다.
 *   amount  그 줄의 증감 — 누적이 아니다
 *   hold    true 면 홀드 줄 — '홀드' 딱지를 단다(금액 색은 다른 줄과 같다)
 * 홀드와 충전금을 '내 몫' 한 금액으로 본다: 줄은 그 금액의 증감, 맨 아래 합계는 증감의 합.
 * (작업완료 때 홀드가 풀리는 줄(−)과 충전금이 오가는 줄(+)은 한 줄로 합쳐 0 — fillLogs)
 */
const LOGS = {
    point: {
        title: '이 오더의 포인트',
        unit : 'P',
        // 목록 화면과 같은 엔드포인트. 0P 줄도 보여준다 — '왜 적립이 안 됐는지' 메모가 거기 있다.
        // 서버는 최신순으로 준다 → 뒤집는다
        load : (ordernum) => $.post('/m/ajax/point/list', { ordernum, usednot: 2, start: 0, length: 200 })
            .then((res) => (res?.data || []).map((r) => ({ date: r.datetime, label: Util.escapeHtml(r.exp).replace(/&lt;br&gt;/g, '<br>'), amount: r.point_val })).reverse()),
    },
    cash: {
        title: '이 오더의 충전금',
        unit : '원',
        // 홀드이력 + 캐시원장을 합친 흐름 (CashQueryService::orderCashRows — 일지 캐시내역과 같은 것)
        load : (ordernum) => $.post(`/m/ajax/order/${ordernum}/cash/logs`)
            .then((res) => (res?.data || []).map((r) => {
                const hold = Number(r.is_hold) === 1;
                return {
                    at    : String(r.datetime),                         // 정렬 · 합칠 짝 찾기용(초까지)
                    date  : dayjs(r.datetime).format('M.D HH:mm'),      // 사용내역 화면과 같은 꼴
                    label : esc(r.rel_action),
                    amount: hold ? r.hold : r.cash,
                    hold,
                };
            })),
    },
};

export default class OrderSheet {
    destroy() {
        $(`#${this.id}`).off();
    }

    init() {
        this.ordernum = parseInt(this.data?.ordernum, 10) || 0;
        this.logs = LOGS[this.data?.logs] || null;
        this.parent?.setTitle?.('오더 상세');

        this.draw();
        this.loadOrder();
        this.loadContacts();
        if (this.logs) this.loadLogs();

        // 연락처 상자 — 닫혀 있으면 카드 어디를 눌러도 펼친다. 펼친 뒤엔 머리줄([연락처 접기])만 접는다
        // (번호를 길게 눌러 복사하다 접히지 않게)
        $(`#${this.id}`).on('click', '.ods-contact', function (e) {
            const $btn = $(this).find('.js-ct-toggle');
            const isOpen = $btn.attr('aria-expanded') === 'true';
            if (isOpen && !$(e.target).closest('.js-ct-toggle').length) return;
            $btn.attr('aria-expanded', String(!isOpen)).find('span').text(isOpen ? '연락처 보기' : '연락처 접기');
            $(this).find('.ods-ct-body').prop('hidden', isOpen);
        });

        $(`#${this.id}`).on('click', '.js-diary', () => {
            window.location.href = `/m/alim/redirect?ordernum=${this.ordernum}`;
        });
    }

    /**
     * ① 오더 요약 한 장 · 연락처 상자(loadContacts) ② 이 오더의 내역(logs 가 있을 때). 맨 아래 [일지보기].
     * 이 시트를 여는 이유는 ②(내역)라서 ①은 짧게 묶는다 — 아주 크게에서도 ②가 첫 화면에 걸리게.
     * 오더번호는 회원에게 정보가 아니라(위 머리말) 장비대금 아래 보조 줄에만 둔다 — CS 문의 때 읽어 줄 값
     */
    draw() {
        $(`#${this.id}`).html(`
        <div class="ods">
            <div class="js-info"><div class="ods-card"><p class="ods-empty">불러오는 중…</p></div></div>
            <div class="js-contacts"></div>
            ${this.logs ? OrderSheet.logsHtml(this.data.logs) : ''}
            <div class="ods-foot">
                <button type="button" class="ods-btn js-diary">일지보기</button>
            </div>
        </div>`);
    }

    loadOrder() {
        const $info = $(`#${this.id}`).find('.js-info');
        const fail = (msg) => {
            $info.html(`<div class="ods-card"><p class="ods-empty">${esc(msg)}</p></div>`);
            $(`#${this.id}`).find('.js-diary').prop('disabled', true);
        };
        $.get(`/m/ajax/order/${this.ordernum}`).done((o) => {
            if (!o || o.status !== 'OK') return fail(o?.status || '오더를 불러오지 못했습니다.');

            const info = $j.F.Order.Info.init(o, typeof MEMBER !== 'undefined' ? MEMBER : {});
            const code = Number(o.order_status) || 1;           // 0 은 접수로 그린다
            const label = (typeof G5 !== 'undefined' && G5.ORDER_STATUS?.[code]) || '';
            const when = dayjs(o.work_time);
            const yoil = '일월화수목금토'[when.day()];

            // 오더종류 딱지 — '어플수주' · '지정발주' 처럼 종류 + 내 쪽. 임의/외부는 서버가 적어 둔 말(임의수주 등)이 정본
            const me = Number(typeof MEMBER !== 'undefined' ? MEMBER.mem_ownercode : 0) || 0;
            const isBal = me > 0 && Number(o.order_owner_num) === me;
            const isSoo = me > 0 && Number(o.worker_owner_num) === me;
            const type = Number(o.order_type) || 0;
            const typeName = (typeof G5 !== 'undefined' && G5.ORDER_TYPE?.[type]) || '';
            const kind = ([91, 94].includes(type) && o.direct_type)
                ? o.direct_type
                : typeName + (isSoo ? '수주' : (isBal ? '발주' : '오더'));
            const payName = (typeof G5 !== 'undefined' && G5.HOW_TO_PAY?.[Number(o.pay_method)]) || '';

            // 보조 한 줄 — 기종 · 수수료 · 오더번호(CS 문의 때 읽어 줄 값)
            const line = (parts) => parts.filter((v) => Util.nvl(v, '') !== '').map(esc).join(' · ');
            const meta = line([info['사용시간'], `${o.car_type || ''} ${info['기종'] || ''}`.trim(), info['수수료율'] !== '' && info['수수료율'] != null ? `수수료 ${info['수수료율']}%` : '', `오더 ${this.ordernum}`]);
            // 전달사항(상세주소 등) — 노란 띠. 없으면 비워 둔 걸 알 수 있게 안내 글
            const note = String(o.request_note || '').replace(/<br\s*\/?>/gi, '\n').trim();

            // 피그마(30272:46253) 꼴을 아주 크게(글자 폭 ~200px)에서도 줄마다 한 줄로 — 한 줄에 세 덩어리를 두지 않는다
            //   ① 일시 ↔ 금액   ② 오더종류 딱지 · 상태 ↔ 결제방식   ③ 주소(혼자 전체 폭)   ④ 사용시간 · 기종 · 수수료 · 오더
            $info.html(`
                <section class="ods-card ods-top">
                    <div class="ods-l1">
                        <span class="ods-when">${when.format('M/D')}<small>(${yoil})</small> ${when.format('HH:mm')}</span>
                        <span class="ods-amt">${Number(o.car_price || 0).toLocaleString()}</span>
                    </div>
                    <div class="ods-l2">
                        ${kind ? `<span class="ods-type">${esc(kind)}</span>` : ''}
                        ${label ? `<span class="ods-st">${esc(label)}</span>` : ''}
                        ${payName ? `<span class="ods-paym">${esc(payName)}</span>` : ''}
                    </div>
                    <p class="ods-addr">${esc(o.site_address)}</p>
                    ${meta ? `<p class="ods-meta">${meta}</p>` : ''}
                </section>
                <div class="ods-note${note ? '' : ' is-empty'}">${note ? esc(note) : '상세주소, 전달사항이 없습니다.'}</div>`);
        }).fail(() => fail('오더를 불러오지 못했습니다.'));
    }

    /**
     * 연락처 상자 — 거래처 · 현장 · (발주자) · 수주자 · 소속자. 보기만 한다(전화·변경 버튼 없음 — 일지에서).
     * 번호를 가리는 규칙은 서버가 일지 상세에서 그대로 따온다(OrderContactService) — 여기서 가리지 않는다.
     * 내 쪽 줄(내가 발주했으면 발주자 · 수주했으면 수주자 · 내가 나갔으면 소속자)은 서버가 뺀다 — 내 번호다
     */
    loadContacts() {
        const $box = $(`#${this.id}`).find('.js-contacts');
        $.get(`/m/ajax/order/${this.ordernum}/contacts`).done((res) => {
            const rows = res?.rows || [];
            if (!res?.ok || !rows.length) return $box.empty();

            const tone = { 수주자: ' is-soo', 소속자: ' is-sosok' };
            // 평소엔 닫아 둔다 — 시트의 본론은 아래 내역이다. [연락처 보기] 를 누르면 펼친다
            $box.html(`
                <section class="ods-contact">
                    <button type="button" class="ods-ct-toggle js-ct-toggle" aria-expanded="false">
                        <span>연락처 보기</span><i class="fas fa-chevron-down" aria-hidden="true"></i>
                    </button>
                    <div class="ods-ct-body" hidden>
                    ${rows.map((r) => {
                        const val = r.phone || r.name
                            ? `${r.phone ? `<span class="ods-ct-phone${tone[r.key] || ''}">${esc(r.phone)}</span>` : ''}`
                              + `${r.name ? `<span class="ods-ct-name${tone[r.key] || ''}">${esc(r.name)}</span>` : ''}`
                              + `${r.phone ? '' : '<span class="ods-ct-none">번호 미등록</span>'}`
                            : '<span class="ods-ct-name">미입력</span><span class="ods-ct-none">번호 미등록</span>';
                        return `<div class="ods-ct-row"><span class="ods-ct-k">${esc(r.label)}</span><span class="ods-ct-v">${val}</span></div>`;
                    }).join('')}
                    </div>
                </section>`);
        }).fail(() => $box.empty());
    }

    loadLogs() {
        OrderSheet.fillLogs($(`#${this.id}`), this.data.logs, this.ordernum);
    }

    /*
     * 내역 카드('이 오더의 충전금' 등)만 따로 그린다 — 일지 > 캐시내역 시트(F/Diary/CashLog)도 같은 카드를 쓴다.
     * 두 시트가 한 벌을 쓰게 static 으로 둔다. 감싸는 .ods 는 부르는 쪽이 둔다(스타일 스코프)
     */

    /** @param {string} kind LOGS 의 키 (point | cash) */
    static logsHtml(kind) {
        return `
            <section class="ods-sec">
                <div class="ods-hd"><h3>${LOGS[kind].title}</h3><span class="ods-sum js-sum"></span></div>
                <ul class="ods-card ods-logs js-logs"><li class="ods-empty">불러오는 중…</li></ul>
            </section>`;
    }

    /** logsHtml 로 그린 카드를 채운다 */
    static fillLogs($root, kind, ordernum) {
        const unit = LOGS[kind].unit;
        LOGS[kind].load(ordernum).then((list) => {
            if (!list.length) {
                $root.find('.js-logs').html('<li class="ods-empty">내역이 없습니다.</li>');
                return;
            }
            const rows = OrderSheet._settle(list);
            const sum = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
            // 합계는 제목 줄 오른쪽 — 맨 아래에 두면 아주 크게에서 위 요약 카드에 밀려 늘 스크롤해야 본다.
            // 홀드가 아직 남아 있으면 이름을 '홀드 중' 으로 — 확정된 돈이 아니다
            const held = list.filter((r) => r.hold).reduce((a, r) => a + (Number(r.amount) || 0), 0);
            $root.find('.js-sum').html(`<span>${held !== 0 ? '홀드 중' : '합계'}</span> ${signed(sum)}${unit}`);
            $root.find('.js-logs').html(rows.map((r) => `
                <li class="ods-log${r.hold ? ' is-hold' : ''}">
                    <div class="ods-log-main">
                        <div class="ods-log-label">${r.hold ? '<span class="ods-badge is-ready">홀드</span>' : ''}${r.label}${r.settled ? '<span class="ods-log-note">홀드 확정</span>' : ''}</div>
                        <div class="ods-log-date">${esc(r.date)}</div>
                    </div>
                    <span class="ods-log-amt">${signed(r.amount)}</span>
                </li>`).join(''));
        }, () => {
            $root.find('.js-logs').html('<li class="ods-empty">내역을 불러오지 못했습니다.</li>');
        });
    }

    /**
     * 시각순으로 세우고, 같은 시각 · 같은 사유의 홀드 줄과 충전금 줄을 한 줄로 합친다(settled).
     * 작업완료 때 홀드가 풀리는 줄(−60,000)과 충전금이 오가는 줄(+60,000)이 동시에 생기는데,
     * 따로 그리면 돈이 빠졌다 들어온 것처럼 읽힌다 — 실제로는 묶여 있던 돈이 확정된 것뿐이라 합쳐 0 으로 둔다.
     * 서버(orderCashRows)는 홀드 줄을 먼저, 충전금 줄을 나중에 붙여 준다(시각순이 아니다)
     */
    static _settle(list) {
        const sorted = list.map((r, i) => ({ ...r, i })).sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : a.i - b.i));
        const out = [];
        sorted.forEach((r) => {
            const pair = r.at && out.find((p) => p.at === r.at && p.label === r.label && p.hold !== r.hold && !p.settled);
            if (pair) {
                pair.amount = (Number(pair.amount) || 0) + (Number(r.amount) || 0);
                pair.hold = false;
                pair.settled = true;
                return;
            }
            out.push({ ...r });
        });
        return out;
    }
}

function signed(n) {
    n = Number(n) || 0;
    return (n > 0 ? '+' : '') + n.toLocaleString();
}

function esc(s) {
    return $('<div>').text(s ?? '').html();
}
